import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { getCheckoutStatus } from '../lib/api';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const maxAttempts = 10;
  const pollInterval = 2000;
  
  const checkPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }
    
    try {
      const response = await getCheckoutStatus(sessionId);
      const data = response.data;
      
      if (data.payment_status === 'paid') {
        setStatus('success');
        setResult(data);
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Still processing, poll again
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Status check error:', error);
      setAttempts(prev => prev + 1);
      if (attempts < maxAttempts - 1) {
        setTimeout(checkPaymentStatus, pollInterval);
      } else {
        setStatus('error');
      }
    }
  };
  
  useEffect(() => {
    if (!sessionId) {
      navigate('/subscription');
      return;
    }
    
    checkPaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="glass-card p-8">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
            <h1 className="text-2xl font-serif font-bold mb-2">Processing Payment</h1>
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="glass-card p-8">
            <div className="relative w-20 h-20 mx-auto mb-6">
              <img src={ONIRVA_ICON} alt="" className="w-full h-full rounded-2xl" />
              <CheckCircle className="absolute -bottom-2 -right-2 w-8 h-8 text-green-500 bg-background rounded-full" />
            </div>
            
            <h1 className="text-2xl font-serif font-bold mb-2">Welcome to ONIRVA {result?.tier === 'deep' ? 'Deep' : 'Plus'}</h1>
            <p className="text-muted-foreground mb-6">
              Your subscription is now active. All premium features are unlocked.
            </p>
            
            <div className="bg-primary/10 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-primary">
                <Sparkles className="w-5 h-5" />
                <span className="font-medium">
                  {result?.tier === 'deep' ? 'Full Intelligence Suite' : 'Unlimited Analysis'}
                </span>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button 
                className="w-full rounded-full"
                onClick={() => navigate('/dashboard')}
              >
                Start Exploring
              </Button>
              <Button 
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/capture')}
              >
                Record a Dream
              </Button>
            </div>
          </div>
        )}
        
        {status === 'expired' && (
          <div className="glass-card p-8">
            <XCircle className="w-16 h-16 mx-auto mb-6 text-muted-foreground" />
            <h1 className="text-2xl font-serif font-bold mb-2">Session Expired</h1>
            <p className="text-muted-foreground mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button 
              className="w-full rounded-full"
              onClick={() => navigate('/subscription')}
            >
              Return to Plans
            </Button>
          </div>
        )}
        
        {status === 'timeout' && (
          <div className="glass-card p-8">
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-muted-foreground" />
            <h1 className="text-2xl font-serif font-bold mb-2">Taking Longer Than Expected</h1>
            <p className="text-muted-foreground mb-6">
              Your payment may still be processing. Please check your email for confirmation or contact support if you do not receive it within a few minutes.
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full rounded-full"
                onClick={() => {
                  setAttempts(0);
                  setStatus('checking');
                  checkPaymentStatus();
                }}
              >
                Check Again
              </Button>
              <Button 
                variant="ghost"
                className="w-full"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}
        
        {status === 'error' && (
          <div className="glass-card p-8">
            <XCircle className="w-16 h-16 mx-auto mb-6 text-destructive" />
            <h1 className="text-2xl font-serif font-bold mb-2">Something Went Wrong</h1>
            <p className="text-muted-foreground mb-6">
              We could not verify your payment. If you were charged, please contact support.
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full rounded-full"
                onClick={() => navigate('/subscription')}
              >
                Return to Plans
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
